import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { speak, cancelSpeech } from "@/lib/tts";
import { api } from "@/lib/api";
import { getLocalDateString } from "@/lib/date";
import { useAgent } from "@/context/AgentContext";

const MAX_TURNS = 6;

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function badgeLabelFor(action, t) {
  if (!action) return null;
  if (action.type === "update_text") return t("ia.badges.updated", { slot: action.slot });
  if (action.type === "toggle")
    return t(action.completed ? "ia.badges.toggled_done" : "ia.badges.toggled_undone", { slot: action.slot });
  if (action.type === "clear") return t("ia.badges.cleared", { slot: action.slot });
  if (action.type === "clear_all") return t("ia.badges.cleared_all");
  return null;
}

function MicButton({ state, onClick, disabled }) {
  const { t } = useTranslation();
  const ringClasses = {
    idle: "border-[rgba(0,229,255,0.4)] shadow-[0_0_30px_rgba(0,229,255,0.2)]",
    listening:
      "border-[rgba(0,229,255,0.9)] shadow-[0_0_40px_rgba(0,229,255,0.55)] animate-pulse-cyan",
    processing:
      "border-[rgba(0,229,255,0.35)] shadow-[0_0_30px_rgba(0,229,255,0.15)]",
    error: "border-[rgba(239,68,68,0.6)] shadow-[0_0_30px_rgba(239,68,68,0.3)]",
  }[state];

  const iconColor = {
    idle: "text-[#00E5FF]",
    listening: "text-[#00E5FF]",
    processing: "text-[#00E5FF]/70",
    error: "text-[#EF4444]",
  }[state];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="ia-mic-button"
      data-state={state}
      aria-label={state === "listening" ? t("ia.mic.stop") : t("ia.mic.start")}
      className={`
        relative h-[120px] w-[120px] rounded-full
        flex items-center justify-center
        bg-white/[0.02] backdrop-blur
        border-[1.5px] transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        hover:scale-[1.03] active:scale-[0.97]
        ${ringClasses}
      `}
    >
      {state === "listening" && (
        <span
          aria-hidden="true"
          className="absolute inset-[-10px] rounded-full border border-[rgba(0,229,255,0.35)] animate-ping"
        />
      )}
      {state === "processing" ? (
        <Loader2 className={`h-11 w-11 ${iconColor} animate-spin`} strokeWidth={1.75} />
      ) : state === "listening" ? (
        <Square className={`h-10 w-10 ${iconColor}`} strokeWidth={1.75} fill="currentColor" />
      ) : (
        <Mic className={`h-11 w-11 ${iconColor}`} strokeWidth={1.75} />
      )}
    </button>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
      data-testid={`bubble-${role}`}
    >
      <div
        className={`
          max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-snug
          ${isUser
            ? "bg-white/[0.06] text-[#E5E7EB] rounded-br-md"
            : "bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.18)] text-[#E5E7EB] rounded-bl-md"}
        `}
      >
        {text}
      </div>
    </div>
  );
}

export default function IAPage() {
  const { t } = useTranslation();
  const {
    supported,
    listening,
    transcript,
    interim,
    error: srError,
    start,
    stop,
    reset,
  } = useSpeechRecognition({ lang: "es-ES" });

  const date = useMemo(() => getLocalDateString(), []);
  const { triggerPerfectDay, pushLogrosSync } = useAgent();

  const [turns, setTurns] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [badge, setBadge] = useState(null);
  const badgeTimerRef = useRef(null);
  const processedRef = useRef("");

  const state = !supported
    ? "error"
    : processing
    ? "processing"
    : listening
    ? "listening"
    : srError
    ? "error"
    : "idle";

  const handleMicClick = useCallback(() => {
    if (!supported || processing) return;
    if (listening) {
      stop();
    } else {
      reset();
      cancelSpeech();
      start();
    }
  }, [supported, processing, listening, start, stop, reset]);

  useEffect(() => {
    const finalText = (transcript || "").trim();
    if (!finalText) return;
    if (listening || processing) return;
    if (finalText === processedRef.current) return;
    processedRef.current = finalText;

    const go = async () => {
      setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "user", text: finalText }]);
      setProcessing(true);
      try {
        const res = await api.post("/ia/interpret", {
          transcript: finalText,
          date,
          tz: getTimezone(),
          source: "voice",
        });
        const data = res.data || {};
        const actions = Array.isArray(data.actions) ? data.actions : [];
        // Note: the LLM `response` text stays in Spanish (branding). Do NOT
        // i18n this fallback either — product decision, see PRD.md Phase 9.
        const response =
          typeof data.response === "string" && data.response.trim()
            ? data.response.trim()
            : "Entendido, Sr. Fernando.";

        setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "assistant", text: response }]);
        speak(response);  // TTS stays in es-ES (branding).

        if (Array.isArray(data.logros)) {
          // Phase 10: array is variable-length (0..50), not fixed at 9.
          pushLogrosSync(data.logros);
        }
        if (data.is_perfect_day_today) {
          const total = Array.isArray(data.logros) ? data.logros.length : undefined;
          triggerPerfectDay(date, total);
        }

        const firstMeaningful = actions.find((a) => a && a.type && a.type !== "none");
        const label = badgeLabelFor(firstMeaningful, t);
        if (label) {
          setBadge(label);
          if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
          badgeTimerRef.current = setTimeout(() => setBadge(null), 2500);
        }
      } catch (e) {
        const status = e?.response?.status;
        const msg = status === 401
          ? t("ia.errors.session_expired")
          : t("ia.errors.generic");
        setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "assistant", text: msg }]);
        speak(msg);  // Still es-ES voice — brand consistency.
      } finally {
        setProcessing(false);
      }
    };
    go();
  }, [transcript, listening, processing, date, pushLogrosSync, triggerPerfectDay, t]);

  useEffect(() => () => cancelSpeech(), []);

  if (!supported) {
    return (
      <div
        className="h-full min-h-[60vh] flex flex-col items-center justify-center text-center px-6"
        data-testid="ia-page"
      >
        <div className="h-16 w-16 rounded-full nx-glass flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-[#F59E0B]" strokeWidth={1.75} />
        </div>
        <h2
          className="mt-5 text-[20px] font-semibold text-[#E5E7EB]"
          data-testid="ia-unsupported-title"
        >
          {t("ia.unsupported.title")}
        </h2>
        <p className="mt-2 text-[13px] text-[#9CA3AF] max-w-[300px] leading-relaxed">
          {t("ia.unsupported.message")}
        </p>
      </div>
    );
  }

  const liveText = listening
    ? (interim || transcript || t("ia.state.listening"))
    : null;

  return (
    <div className="pt-2 pb-2 flex flex-col items-center" data-testid="ia-page">
      <div className="w-full text-center mb-5">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]/70">
          {t("ia.tab.title")}
        </p>
        <h2 className="mt-1 text-[18px] font-semibold text-[#E5E7EB]" data-testid="ia-title">
          {t("ia.tab.subtitle")}
        </h2>
      </div>

      <MicButton
        state={state}
        onClick={handleMicClick}
        disabled={!supported || processing}
      />

      <p className="mt-4 text-[12px] text-[#9CA3AF] min-h-[16px]" data-testid="ia-state-label">
        {state === "listening"
          ? t("ia.state.listening")
          : state === "processing"
          ? t("ia.state.processing")
          : srError === "not-allowed" || srError === "service-not-allowed"
          ? t("ia.state.mic_blocked")
          : srError
          ? t("ia.state.error")
          : t("ia.state.idle")}
      </p>

      {liveText ? (
        <div
          className="mt-4 w-full rounded-xl bg-white/[0.03] border border-white/[0.05] px-4 py-3 text-[14px] text-[#E5E7EB]/80 italic"
          data-testid="ia-live-transcript"
        >
          {liveText}
        </div>
      ) : null}

      {badge ? (
        <div
          className="mt-4 px-3 py-1.5 rounded-full bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.35)] flex items-center gap-1.5 text-[12px] text-[#00E5FF]"
          data-testid="ia-action-badge"
        >
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.25} />
          {badge}
        </div>
      ) : null}

      <div className="mt-5 w-full flex flex-col gap-2" data-testid="ia-history">
        {turns.slice(-MAX_TURNS).map((turn, i) => (
          <Bubble key={i} role={turn.role} text={turn.text} />
        ))}
      </div>

      {turns.length === 0 && !listening && !processing ? (
        <div
          className="mt-6 w-full rounded-xl bg-white/[0.02] border border-white/[0.05] px-4 py-3"
          data-testid="ia-hint"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-1.5">
            {t("ia.hint.label")}
          </p>
          <ul className="space-y-1 text-[12.5px] text-[#E5E7EB]/80 leading-relaxed">
            <li>• {t("ia.hint.example_1")}</li>
            <li>• {t("ia.hint.example_2")}</li>
            <li>• {t("ia.hint.example_3")}</li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
